import { useState, useCallback } from 'react'
import { useAppSelector } from '../store/hooks'
import './DefaultPromptDisplay.css'

interface DefaultPromptDisplayProps {
  minContainers: number
  maxContainers: number
}

function EditableNumber({ label, value, field, testId }: { label: string; value: number; field: string; testId: string }) {
  const isSignedIn = useAppSelector((state) => !!state.auth.user)
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [current, setCurrent] = useState(value)
  const [saving, setSaving] = useState(false)

  // Keep in sync with props
  if (!editing && current !== value) setCurrent(value)

  const handleSave = useCallback(async () => {
    const val = parseInt(editValue, 10)
    if (isNaN(val) || val < 0) return
    setSaving(true)
    try {
      const resp = await fetch('/api/update-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: val }),
      })
      if (resp.ok) {
        setCurrent(val)
        setEditing(false)
      }
    } catch { /* ignore */ }
    setSaving(false)
  }, [editValue, field])

  return (
    <div className="default-prompt__row">
      <dt className="default-prompt__label">{label}</dt>
      <dd className="default-prompt__value" data-testid={testId}>
        {editing ? (
          <span className="default-prompt__edit-inline">
            <input
              type="number"
              min={0}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="default-prompt__edit-input"
              autoFocus
            />
            <button className="default-prompt__edit-save" onClick={handleSave} disabled={saving}>
              {saving ? '...' : 'Save'}
            </button>
            <button className="default-prompt__edit-cancel" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </span>
        ) : (
          <span className="default-prompt__value-row">
            {current}
            {isSignedIn && (
              <button className="default-prompt__edit-btn" onClick={() => { setEditValue(String(current)); setEditing(true) }}>
                Edit
              </button>
            )}
          </span>
        )}
      </dd>
    </div>
  )
}

function DefaultPromptDisplay({ minContainers, maxContainers }: DefaultPromptDisplayProps) {
  return (
    <section className="default-prompt" data-testid="default-prompt" aria-labelledby="default-prompt-heading">
      <h2 className="default-prompt__title" id="default-prompt-heading">Build Configuration</h2>
      <dl className="default-prompt__list">
        <EditableNumber label="Min Containers" value={minContainers} field="minContainers" testId="min-containers-value" />
        <EditableNumber label="Max Containers (Global)" value={maxContainers} field="maxContainers" testId="max-containers-value" />
      </dl>
    </section>
  )
}

export default DefaultPromptDisplay
